import React, { useState } from 'react';
import { Box, Text } from 'ink';
import type { PluginCommandApprovalInputState } from '../types';
import { useKeyHandler } from '../lib/key_handler';
import { useTheme } from '../lib/theme';

export const pluginCommandApprovalFooterHint = (): string | null =>
  '←/→ to choose • Enter to confirm • Esc to reject';

export interface PluginCommandApprovalCallbacks {
  /** Approve and run the command; the approval is persisted. */
  onApprove: () => void;
  /** Enter on Reject and Esc are equivalent: nothing persisted, command not run. */
  onReject: () => void;
}

interface PluginCommandApprovalInputProps {
  input: PluginCommandApprovalInputState;
  callbacks: PluginCommandApprovalCallbacks;
}

type Decision = 'approve' | 'reject';

export const PluginCommandApprovalInput: React.FC<PluginCommandApprovalInputProps> = ({
  input,
  callbacks,
}) => {
  const { tokens } = useTheme();
  const [decision, setDecision] = useState<Decision>('approve');

  useKeyHandler((event) => {
    if (event.eventType !== 'press') return;

    if (event.name === 'escape') {
      callbacks.onReject();
      event.stopPropagation();
      return;
    }

    if (event.name === 'return') {
      if (decision === 'approve') {
        callbacks.onApprove();
      } else {
        callbacks.onReject();
      }
      event.stopPropagation();
      return;
    }

    if (event.name === 'left' || event.name === 'up') {
      setDecision('approve');
      event.stopPropagation();
      return;
    }

    if (event.name === 'right' || event.name === 'down') {
      setDecision('reject');
      event.stopPropagation();
    }
  });

  return (
    <Box flexDirection="column">
      <Box
        borderStyle="round"
        borderColor={tokens.text.warning}
        paddingX={2}
        paddingY={1}
        flexDirection="column"
      >
        <Box marginBottom={1}>
          <Text bold color={tokens.text.warning}>
            ⚠ Plugin command approval required
          </Text>
        </Box>

        <Box marginBottom={1} flexDirection="column">
          <Text>
            /{input.commandName} (from {input.pluginId}) wants to run a shell command:
          </Text>
        </Box>

        <Box marginBottom={1}>
          <Text color={tokens.text.accent}>{input.run}</Text>
        </Box>

        {input.envLines.length > 0 ? (
          <Box marginBottom={1} flexDirection="column">
            <Text dimColor>with environment:</Text>
            {input.envLines.map((line) => (
              <Text key={line} color={tokens.text.accent}>
                {line}
              </Text>
            ))}
          </Box>
        ) : null}

        <Box gap={2} marginBottom={1}>
          <Text
            bold={decision === 'approve'}
            color={decision === 'approve' ? tokens.text.success : tokens.text.subtle}
          >
            {decision === 'approve' ? '› [ Approve ]' : '  [ Approve ]'}
          </Text>
          <Text
            bold={decision === 'reject'}
            color={decision === 'reject' ? tokens.text.danger : tokens.text.subtle}
          >
            {decision === 'reject' ? '› [ Reject ]' : '  [ Reject ]'}
          </Text>
        </Box>

        <Box>
          <Text dimColor>
            Approving is remembered until the plugin changes the command. Rejecting runs nothing.
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
